import axios from 'axios';
import {v4 as uuidv4} from 'uuid';
import Auth from '../../../auth/auth';
import {canonicalEdgeBetween, findConnectingEdge} from '../util/graphModel';
import {deriveViewLineage} from '../util/deriveViewLineage';

// ---- Action types ----
export const LOAD_MAP = 'lineage/LOAD_MAP';
export const SET_MAP_PROPERTY = 'lineage/SET_MAP_PROPERTY';
export const APPLY_NODE_CHANGES = 'lineage/APPLY_NODE_CHANGES';
export const APPLY_EDGE_CHANGES = 'lineage/APPLY_EDGE_CHANGES';
export const ADD_NODE = 'lineage/ADD_NODE';
export const UPDATE_NODE = 'lineage/UPDATE_NODE';
export const ADD_EDGE = 'lineage/ADD_EDGE';
export const UPDATE_EDGE = 'lineage/UPDATE_EDGE';
export const REMOVE = 'lineage/REMOVE';
export const ADD_ELEMENT = 'lineage/ADD_ELEMENT';
export const UPDATE_ELEMENT = 'lineage/UPDATE_ELEMENT';
export const ADD_TABLE = 'lineage/ADD_TABLE';
export const UPDATE_TABLE = 'lineage/UPDATE_TABLE';
export const UPDATE_TABLE_POS = 'lineage/UPDATE_TABLE_POS';
export const UPDATE_GROUP_POS = 'lineage/UPDATE_GROUP_POS';
export const UPDATE_DATA_NODE_POS = 'lineage/UPDATE_DATA_NODE_POS';
export const ADD_MAPPING = 'lineage/ADD_MAPPING';
export const UPDATE_MAPPING = 'lineage/UPDATE_MAPPING';
export const GROUP_NODES = 'lineage/GROUP_NODES';
export const UNGROUP_NODE = 'lineage/UNGROUP_NODE';
export const TOGGLE_COLLAPSE = 'lineage/TOGGLE_COLLAPSE';
export const SET_SELECTION = 'lineage/SET_SELECTION';
export const CLEAR_SELECTION = 'lineage/CLEAR_SELECTION';
export const SET_LINEAGE_VIEW_TYPE = 'lineage/SET_LINEAGE_VIEW_TYPE';
export const SET_HIGHLIGHT_MODE = 'lineage/SET_HIGHLIGHT_MODE';
export const OPEN_DRAWER = 'lineage/OPEN_DRAWER';
export const CLOSE_DRAWER = 'lineage/CLOSE_DRAWER';
export const SET_VIEW_MODE = 'lineage/SET_VIEW_MODE';
export const COMMIT_HISTORY = 'lineage/COMMIT_HISTORY';
export const UNDO = 'lineage/UNDO';
export const REDO = 'lineage/REDO';
export const MARK_DIRTY = 'lineage/MARK_DIRTY';
export const MERGE_VIEW_LINEAGE = 'lineage/MERGE_VIEW_LINEAGE';
export const IMPORT_LINEAGE = 'lineage/IMPORT_LINEAGE';
export const NOTIFY = 'lineage/NOTIFY';
export const DISMISS_NOTIFY = 'lineage/DISMISS_NOTIFY';

// ---- Persistence action types ----
export const SAVE_REQUEST = 'lineage/SAVE_REQUEST';
export const SAVE_SUCCESS = 'lineage/SAVE_SUCCESS';
export const SAVE_FAILURE = 'lineage/SAVE_FAILURE';
export const PUBLISH_REQUEST = 'lineage/PUBLISH_REQUEST';
export const PUBLISH_SUCCESS = 'lineage/PUBLISH_SUCCESS';
export const PUBLISH_FAILURE = 'lineage/PUBLISH_FAILURE';

// ---- Characteristics action types ----
export const CHAR_LOAD_SUCCESS = 'lineage/CHAR_LOAD_SUCCESS';
// Slice 6c-3b. There was no failure action at all — the catch reported success with an empty list.
export const CHAR_LOAD_FAILURE = 'lineage/CHAR_LOAD_FAILURE';
export const CHAR_CREATE_SUCCESS = 'lineage/CHAR_CREATE_SUCCESS';
export const CHAR_UPDATE_SUCCESS = 'lineage/CHAR_UPDATE_SUCCESS';
export const CHAR_DELETE_SUCCESS = 'lineage/CHAR_DELETE_SUCCESS';

// ---- Node Refs action types ----
export const NODEREF_LOAD_SUCCESS = 'lineage/NODEREF_LOAD_SUCCESS';
export const NODEREF_CREATE_SUCCESS = 'lineage/NODEREF_CREATE_SUCCESS';
export const NODEREF_UPDATE_SUCCESS = 'lineage/NODEREF_UPDATE_SUCCESS';
export const NODEREF_DELETE_SUCCESS = 'lineage/NODEREF_DELETE_SUCCESS';

// ---- Global settings action types ----
export const GLOBAL_SETTINGS_LOAD_SUCCESS = 'lineage/GLOBAL_SETTINGS_LOAD_SUCCESS';
export const GLOBAL_SETTINGS_SAVE_SUCCESS = 'lineage/GLOBAL_SETTINGS_SAVE_SUCCESS';
export const SET_GLOBAL_THEME = 'lineage/SET_GLOBAL_THEME';

export const SET_DISPLAY_MODE = 'lineage/SET_DISPLAY_MODE';
export const CLEAR_DATA_VIEW_LAYOUT = 'lineage/CLEAR_DATA_VIEW_LAYOUT';

// ---- DD picker action types ----
export const DD_SCHEMAS_SUCCESS = 'lineage/DD_SCHEMAS_SUCCESS';
export const DD_OBJECTS_SUCCESS = 'lineage/DD_OBJECTS_SUCCESS';
export const DD_COLUMNS_SUCCESS = 'lineage/DD_COLUMNS_SUCCESS';
export const DD_CONNECTIONS_SUCCESS = 'lineage/DD_CONNECTIONS_SUCCESS';

// ---- Auth helper (matches impactor.actions.js pattern) ----
const auth = new Auth();
const {getAccessToken} = auth;
const headers = () => ({
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'x-access-token': getAccessToken(),
});

// ---- Single-flight save queue state (module-level, not Redux — it's async coordination) ----
// ponytail: module-level flags rather than a class because there is exactly one canvas per session
let _saveInFlight = false;
let _trailingSave = null; // {explicit: bool} | null

// ---- Load / save ----

/** Purge orphaned lineage-map-* localStorage keys left from the old localStorage-only era. */
const purgeStaleLocalStorage = () => {
  try {
    Object.keys(localStorage)
        .filter((k) => k.startsWith('lineage-map-'))
        .forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    // storage may be unavailable in some embeddings
  }
};

export const loadMap = (mapId) => async (dispatch) => {
  purgeStaleLocalStorage();
  if (!mapId) {
    const newId = `map-${uuidv4()}`;
    const payload = {id: newId};
    dispatch({type: LOAD_MAP, payload});
    return payload;
  }
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lineage/diagram/${mapId}`,
        {headers: headers()},
    );
    const json = res.data;
    if (json && json.status !== false && json.response) {
      dispatch({type: LOAD_MAP, payload: json.response});
      return json.response;
    }
    // Diagram not found yet (new) — start blank with the requested id
    const payload = {id: mapId};
    dispatch({type: LOAD_MAP, payload});
    return payload;
  } catch (e) {
    const payload = {id: mapId};
    dispatch({type: LOAD_MAP, payload});
    return payload;
  }
};

export const setMapProperty = (key, value) => ({type: SET_MAP_PROPERTY, payload: {key, value}});
export const setDisplayMode = (mode) => ({type: SET_DISPLAY_MODE, payload: mode});

/**
 * Core upsert: POST /lineage/diagram (first save by id) or PUT /lineage/diagram/:id.
 * Returns the updated lockVersion. Handles conflict and forbidden responses.
 * explicit=true signals the backend to write an audit checkpoint.
 */
const _persistMap = async (map, explicit, dispatch) => {
  dispatch({type: SAVE_REQUEST});
  try {
    const body = {...map, explicit: explicit || undefined};
    const isNew = !map.lockVersion && map.lockVersion !== 0;
    const res = isNew ?
      await axios.post(`${process.env.REACT_APP_API_URL}/lineage/diagram`, body, {headers: headers()}) :
      await axios.put(`${process.env.REACT_APP_API_URL}/lineage/diagram/${map.id}`, body, {headers: headers()});
    const json = res.data;
    if (json && json.status === false) {
      dispatch({type: SAVE_FAILURE, payload: json.error || 'save failed'});
      return {ok: false, conflict: json.error === 'conflict', forbidden: json.error === 'forbidden'};
    }
    const newLockVersion = json && json.response && json.response.lockVersion != null ?
      json.response.lockVersion :
      (map.lockVersion != null ? map.lockVersion + 1 : 1);
    dispatch({type: SAVE_SUCCESS, payload: {lockVersion: newLockVersion}});
    return {ok: true, lockVersion: newLockVersion};
  } catch (e) {
    dispatch({type: SAVE_FAILURE, payload: e.message || 'network error'});
    return {ok: false};
  }
};

/**
 * autoSaveMap — silent background save. Runs through the single-flight queue:
 * if a save is already in flight, schedule a trailing save instead of racing.
 * The trailing save carries explicit=true if the user pressed Save during the flight.
 */
export const autoSaveMap = () => async (dispatch, getState) => {
  if (_saveInFlight) {
    if (!_trailingSave) _trailingSave = {explicit: false};
    return;
  }
  _saveInFlight = true;
  const {map} = getState().lineageReducer;
  const result = await _persistMap(map, false, dispatch);
  _saveInFlight = false;

  if (_trailingSave) {
    const trail = _trailingSave;
    _trailingSave = null;
    const {map: latestMap} = getState().lineageReducer;
    _saveInFlight = true;
    await _persistMap(latestMap, trail.explicit, dispatch);
    _saveInFlight = false;
  }
  return result;
};

/**
 * saveMap — explicit save (Cmd+S / Save button). If a save is in flight, marks the
 * trailing slot as explicit so the audit checkpoint fires when the in-flight finishes.
 */
export const saveMap = () => async (dispatch, getState) => {
  if (_saveInFlight) {
    // Upgrade or create trailing save with explicit flag
    _trailingSave = {explicit: true};
    return;
  }
  _saveInFlight = true;
  const {map} = getState().lineageReducer;
  const result = await _persistMap(map, true, dispatch);
  _saveInFlight = false;

  if (_trailingSave) {
    const trail = _trailingSave;
    _trailingSave = null;
    const {map: latestMap} = getState().lineageReducer;
    _saveInFlight = true;
    await _persistMap(latestMap, trail.explicit, dispatch);
    _saveInFlight = false;
  }
  return result;
};

export const publishMap = () => async (dispatch, getState) => {
  dispatch({type: PUBLISH_REQUEST});
  const {map} = getState().lineageReducer;
  try {
    const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/lineage/diagram/${map.id}/publish`,
        {lockVersion: map.lockVersion},
        {headers: headers()},
    );
    const json = res.data;
    if (json && json.status === false) {
      dispatch({type: PUBLISH_FAILURE, payload: json.error || 'publish failed'});
      return {ok: false, error: json.error};
    }
    dispatch({type: PUBLISH_SUCCESS, payload: json.response || {}});
    return {ok: true};
  } catch (e) {
    dispatch({type: PUBLISH_FAILURE, payload: e.message || 'network error'});
    return {ok: false};
  }
};

// ---- Characteristics CRUD ----

export const loadCharacteristics = (force = false) => async (dispatch, getState) => {
  if (!force && (getState().lineageReducer.characteristics || []).length > 0) return;
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lineage/characteristics`,
        {headers: headers()},
    );
    const json = res.data;
    dispatch({type: CHAR_LOAD_SUCCESS, payload: Array.isArray(json.response) ? json.response : []});
  } catch (e) {
    // SLICE 6c-3b — A REFUSAL IS NOT AN EMPTY LIST.
    //
    // This dispatched CHAR_LOAD_SUCCESS with [] on every error, so a 403 was indistinguishable from
    // "there are no characteristics": empty palette, no message, nothing in the console. That is
    // precisely how the gap 6c-3b fixes would have reached a user — `/lineage` is now enforced, and
    // `GET /characteristics` needs `lineage.characteristic:read`, which `data_custodian` and
    // `data_champion` did not hold until migration 20261023000001.
    //
    // 403 is called out separately because the remedy differs: a permission problem needs a grant,
    // not a retry.
    dispatch({
      type: CHAR_LOAD_FAILURE,
      payload: e && e.response && e.response.status === 403 ?
        'You do not have permission to read lineage characteristics.' :
        'Could not load lineage characteristics.',
    });
  }
};

export const createCharacteristic = (data) => async (dispatch) => {
  const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/lineage/characteristics`,
      data,
      {headers: headers()},
  );
  const json = res.data;
  if (json && json.response) dispatch({type: CHAR_CREATE_SUCCESS, payload: json.response});
  return json;
};

export const updateCharacteristic = (id, data) => async (dispatch) => {
  const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/lineage/characteristics/${id}`,
      data,
      {headers: headers()},
  );
  const json = res.data;
  if (json && json.response) dispatch({type: CHAR_UPDATE_SUCCESS, payload: json.response});
  return json;
};

export const deleteCharacteristic = (id) => async (dispatch) => {
  await axios.delete(
      `${process.env.REACT_APP_API_URL}/lineage/characteristics/${id}`,
      {headers: headers()},
  );
  dispatch({type: CHAR_DELETE_SUCCESS, payload: id});
};

// ---- Node Refs CRUD ----

export const loadNodeRefs = (force = false) => async (dispatch, getState) => {
  if (!force && (getState().lineageReducer.nodeRefs || []).length > 0) return;
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lineage/node-refs`,
        {headers: headers()},
    );
    const json = res.data;
    dispatch({type: NODEREF_LOAD_SUCCESS, payload: Array.isArray(json.response) ? json.response : []});
  } catch (e) {
    dispatch({type: NODEREF_LOAD_SUCCESS, payload: []});
  }
};

export const createNodeRef = (data) => async (dispatch) => {
  const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/lineage/node-refs`,
      data,
      {headers: headers()},
  );
  const json = res.data;
  if (json && json.response) dispatch({type: NODEREF_CREATE_SUCCESS, payload: json.response});
  return json;
};

export const updateNodeRef = (id, data) => async (dispatch) => {
  const res = await axios.put(
      `${process.env.REACT_APP_API_URL}/lineage/node-refs/${id}`,
      data,
      {headers: headers()},
  );
  const json = res.data;
  // The API returns only {id}; merge the submitted fields so the edit reflects in the store
  // immediately (store shape is camelCase, matching the form + toNodeRef).
  if (json && json.status !== false) dispatch({type: NODEREF_UPDATE_SUCCESS, payload: {id, ...data}});
  return json;
};

export const deleteNodeRef = (id) => async (dispatch) => {
  await axios.delete(
      `${process.env.REACT_APP_API_URL}/lineage/node-refs/${id}`,
      {headers: headers()},
  );
  dispatch({type: NODEREF_DELETE_SUCCESS, payload: id});
};

// ---- DD pickers (guarded against duplicate fetch) ----

export const fetchDdSchemas = () => async (dispatch, getState) => {
  if ((getState().lineageReducer.ddSchemas || []).length > 0) return;
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dataDictionary/schemas`,
        {headers: headers()},
    );
    const json = res.data;
    dispatch({type: DD_SCHEMAS_SUCCESS, payload: Array.isArray(json.response) ? json.response : []});
  } catch (e) {
    dispatch({type: DD_SCHEMAS_SUCCESS, payload: []});
  }
};

export const fetchDdObjects = (schemaId) => async (dispatch, getState) => {
  if (getState().lineageReducer.ddObjects[schemaId]) return;
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dataDictionary/objects/${schemaId}`,
        {headers: headers()},
    );
    const json = res.data;
    const objects = Array.isArray(json.response) ? json.response : [];
    dispatch({type: DD_OBJECTS_SUCCESS, payload: {schemaId, objects}});
  } catch (e) {
    dispatch({type: DD_OBJECTS_SUCCESS, payload: {schemaId, objects: []}});
  }
};

export const fetchDdColumns = (objectId) => async (dispatch, getState) => {
  if (getState().lineageReducer.ddColumns[objectId]) return;
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dataDictionary/columns/${objectId}`,
        {headers: headers()},
    );
    const json = res.data;
    const columns = Array.isArray(json.response) ? json.response : [];
    dispatch({type: DD_COLUMNS_SUCCESS, payload: {objectId, columns}});
  } catch (e) {
    dispatch({type: DD_COLUMNS_SUCCESS, payload: {objectId, columns: []}});
  }
};

export const fetchConnections = () => async (dispatch, getState) => {
  if ((getState().lineageReducer.connections || []).length > 0) return;
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/manageDb/getDbConnection`,
        {headers: headers()},
    );
    const json = res.data;
    const list = Array.isArray(json.response) ? json.response : (Array.isArray(json) ? json : []);
    dispatch({type: DD_CONNECTIONS_SUCCESS, payload: list});
  } catch (e) {
    dispatch({type: DD_CONNECTIONS_SUCCESS, payload: []});
  }
};

// ---- Global settings ----

export const loadGlobalSettings = () => async (dispatch) => {
  try {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lineage/settings`,
        {headers: headers()},
    );
    dispatch({type: GLOBAL_SETTINGS_LOAD_SUCCESS, payload: {theme: res.data.response.theme}});
  } catch (e) {
    // non-fatal: canvas falls back to mergeTheme defaults
  }
};

export const saveGlobalSettings = (theme) => async (dispatch) => {
  try {
    const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/lineage/settings`,
        {theme},
        {headers: headers()},
    );
    dispatch({type: GLOBAL_SETTINGS_SAVE_SUCCESS, payload: {theme: res.data.response.theme}});
    dispatch({type: SET_GLOBAL_THEME, payload: res.data.response.theme});
  } catch (e) {
    return Promise.reject(e);
  }
};

export const fetchCharacteristicUsage = (id) =>
  async () => {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lineage/characteristics/${id}/usage`,
        {headers: headers()},
    );
    return res.data.response;
  };

export const fetchNodeRefUsage = (id) =>
  async () => {
    const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lineage/node-refs/${id}/usage`,
        {headers: headers()},
    );
    return res.data.response;
  };

// ---- React Flow change streams (controlled mode) ----
export const applyNodeChanges = (changes) => ({type: APPLY_NODE_CHANGES, payload: {changes}});
export const applyEdgeChanges = (changes) => ({type: APPLY_EDGE_CHANGES, payload: {changes}});
export const commitHistory = () => ({type: COMMIT_HISTORY});
export const markDirty = () => ({type: MARK_DIRTY});

// ---- Nodes ----
export const addNode = (node) => ({type: ADD_NODE, payload: node});
export const updateNode = (nodeId, updates) => ({type: UPDATE_NODE, payload: {nodeId, updates}});
export const removeNode = (nodeId) => ({type: REMOVE, payload: {target: {node: nodeId}}});

export const createNode = (appType, position) => (dispatch) => {
  const id = `node-${uuidv4()}`;
  dispatch(addNode({
    id,
    kind: 'app',
    type: 'appNode',
    position,
    data: {
      label: '', appType: appType || 'application', details: {}, dataElements: [],
      characteristicId: null, isSource: false, isDestination: false,
      mapping: {type: 'none', connectionId: null, schemaId: null},
    },
  }));
  dispatch(openDrawer('edit', id));
  return id;
};

// ---- Edges ----
export const addEdge = (edge) => ({type: ADD_EDGE, payload: edge});
export const updateEdge = (edgeId, updates) => ({type: UPDATE_EDGE, payload: {edgeId, updates}});
export const removeEdge = (edgeId) => ({type: REMOVE, payload: {target: {edge: edgeId}}});

export const cycleEdgeDirection = (edgeId) => (dispatch, getState) => {
  const edge = getState().lineageReducer.map.edges.find((e) => e.id === edgeId);
  if (!edge) return;
  const order = {forward: 'backward', backward: 'bidirectional', bidirectional: 'forward'};
  dispatch(updateEdge(edgeId, {direction: order[edge.direction] || 'backward'}));
};

export const connectNodes = (source, target, sourceHandle, targetHandle, direction = 'forward') => (dispatch, getState) => {
  if (source === target) return null;
  const {edges} = getState().lineageReducer.map;
  const existing = canonicalEdgeBetween(edges, source, target);
  if (existing) {
    if (existing.direction !== 'bidirectional') {
      dispatch(updateEdge(existing.id, {direction: 'bidirectional'}));
    }
    return existing.id;
  }
  const id = `edge-${uuidv4()}`;
  dispatch(addEdge({id, source, target, sourceHandle, targetHandle, direction, label: '', data: {}}));
  return id;
};

// ---- Tables ----
export const addTable = (nodeId, name) => ({
  type: ADD_TABLE,
  payload: {nodeId, table: {id: `tbl-${uuidv4()}`, name: name || '', dataElements: [], objectId: null}},
});
export const updateTable = (nodeId, tableId, updates) => ({type: UPDATE_TABLE, payload: {nodeId, tableId, updates}});
export const removeTable = (nodeId, tableId) => ({type: REMOVE, payload: {target: {table: {nodeId, tableId}}}});
// Data-view card position (stored on node.data.details.dvMeta[tableId].dataPos).
export const updateTablePos = (nodeId, tableId, pos) => ({type: UPDATE_TABLE_POS, payload: {nodeId, tableId, pos}});
// Data-view group (whole cluster) position (stored on node.data.details.dvGroupPos).
export const updateGroupPos = (nodeId, pos) => ({type: UPDATE_GROUP_POS, payload: {nodeId, pos}});
// Data-view position of a top-level plain app node (stored on node.data.details.dvNodePos).
export const updateDataNodePos = (nodeId, pos) => ({type: UPDATE_DATA_NODE_POS, payload: {nodeId, pos}});
// Clear all data-view layout overrides so the auto-spread runs fresh on the next render.
export const clearDataViewLayout = () => ({type: CLEAR_DATA_VIEW_LAYOUT});

// ---- Data elements ----
export const addElement = (nodeId, element, tableId = null) => ({
  type: ADD_ELEMENT,
  payload: {
    nodeId, tableId,
    element: {id: `el-${uuidv4()}`, name: '', displayName: '', dataType: '', description: '', columnId: null, isCde: false, ...element},
  },
});
export const updateElement = (nodeId, elementId, updates, tableId = null) =>
  ({type: UPDATE_ELEMENT, payload: {nodeId, tableId, elementId, updates}});
export const removeElement = (nodeId, elementId, tableId = null) =>
  ({type: REMOVE, payload: {target: {element: {nodeId, elementId, tableId}}}});

// ---- Element mappings ----
export const addMapping = (mapping) => ({type: ADD_MAPPING, payload: {id: `map-${uuidv4()}`, direction: 'forward', ...mapping}});
export const updateMapping = (mappingId, updates) => ({type: UPDATE_MAPPING, payload: {mappingId, updates}});
export const removeMapping = (mappingId) => ({type: REMOVE, payload: {target: {mapping: mappingId}}});

export const cycleMappingDirection = (mappingId) => (dispatch, getState) => {
  const m = getState().lineageReducer.map.mappings.find((x) => x.id === mappingId);
  if (!m) return;
  const order = {forward: 'backward', backward: 'bidirectional', bidirectional: 'forward'};
  dispatch(updateMapping(mappingId, {direction: order[m.direction] || 'backward'}));
};

export const connectElements = (source, target, direction = 'forward', extra = {}) => (dispatch, getState) => {
  const sameEl = source.nodeId === target.nodeId &&
    (source.tableId || null) === (target.tableId || null) && source.elementId === target.elementId;
  if (sameEl) return;

  const {edges, nodes} = getState().lineageReducer.map;
  let edgeId;
  if (source.nodeId === target.nodeId) {
    edgeId = `self-${source.nodeId}`;
  } else {
    const edge = findConnectingEdge(nodes, edges, source.nodeId, target.nodeId);
    if (edge) {
      edgeId = edge.id;
    } else {
      edgeId = `edge-${uuidv4()}`;
      dispatch(addEdge({
        id: edgeId, source: source.nodeId, target: target.nodeId,
        sourceHandle: null, targetHandle: null, direction, label: '', data: {autoCreated: true},
      }));
    }
  }
  dispatch(addMapping({
    edgeId,
    sourceNodeId: source.nodeId, sourceTableId: source.tableId || null, sourceElementId: source.elementId,
    targetNodeId: target.nodeId, targetTableId: target.tableId || null, targetElementId: target.elementId,
    direction, transformation: extra.transformation || '', label: extra.label || '',
  }));
};

// ---- Grouping ----
export const groupSelected = (memberIds) => (dispatch) => {
  if (!memberIds || memberIds.length < 2) return;
  dispatch({type: GROUP_NODES, payload: {memberIds, groupId: `group-${uuidv4()}`}});
};
export const ungroup = (groupId) => ({type: UNGROUP_NODE, payload: {groupId}});
export const toggleCollapse = (groupId) => ({type: TOGGLE_COLLAPSE, payload: {groupId}});

// ---- Selection / highlight ----
export const selectNode = (nodeId) => ({type: SET_SELECTION, payload: {nodeId}});
export const selectElement = (nodeId, elementId, tableId = null) =>
  ({type: SET_SELECTION, payload: {nodeId, elementId, tableId}});
export const clearSelection = () => ({type: CLEAR_SELECTION});
export const setHighlightMode = (mode) => ({type: SET_HIGHLIGHT_MODE, payload: mode});

// ---- View toggles ----
export const setLineageViewType = (viewType) => ({type: SET_LINEAGE_VIEW_TYPE, payload: viewType});
export const setViewMode = (mode) => ({type: SET_VIEW_MODE, payload: mode});

// ---- Drawer ----
export const openDrawer = (mode, nodeId, elementId = null, tableId = null) =>
  ({type: OPEN_DRAWER, payload: {mode, nodeId, elementId, tableId}});
export const closeDrawer = () => ({type: CLOSE_DRAWER});

// ---- Notifications ----
export const notify = (message, actionLabel = null, actionFn = null) =>
  ({type: NOTIFY, payload: {message, actionLabel, actionFn}});
export const dismissNotify = () => ({type: DISMISS_NOTIFY});

// ---- View-lineage auto-expansion (Req 14) ----

// ponytail: module-level set (one canvas per session) prevents double-fire on double-select
const _viewLineageInFlight = new Set();

/**
 * Fetch view lineage for a VIEW object and merge derived source nodes/edges/mappings into
 * the canvas as a single history commit so one undo removes all of them.
 *
 * @param {string} dbNodeId           Canvas node id of the database node
 * @param {string} viewTableId        Table slot id on that node holding the view
 * @param {string} selectedObjectId   dd objectId of the selected VIEW (viewId in response)
 * @param {string} viewName           Human-readable view name (for toasts)
 * @param {string} instanceId         DD instance id (from the node's mapped schema)
 */
export const expandViewLineage = (dbNodeId, viewTableId, selectedObjectId, viewName, instanceId) =>
  async (dispatch, getState) => {
    const key = `${dbNodeId}:${viewTableId}`;
    if (_viewLineageInFlight.has(key)) return;
    _viewLineageInFlight.add(key);

    try {
      const params = new URLSearchParams({viewNames: viewName});
      if (instanceId) params.set('instanceId', instanceId);
      const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dataDictionary/views/lineage?${params}`,
          {headers: headers()},
      );
      const json = res.data;

      if (!json || json.status === false || !json.response || !Array.isArray(json.response.views)) {
        dispatch(notify(`No lineage derivable for ${viewName}`));
        return;
      }

      const responseView = json.response.views.find((v) => v.viewId === selectedObjectId);
      if (!responseView || !responseView.sources || responseView.sources.length === 0) {
        dispatch(notify(`No lineage derivable for ${viewName}`));
        return;
      }

      // Check if all sources are METADATA_ONLY with zero column mappings
      const allMetadataOnly = responseView.sources.every((src) =>
        (src.columnMappings || []).every((cm) => cm.extractionMethod === 'METADATA_ONLY'));
      if (allMetadataOnly) {
        dispatch(notify(`No lineage derivable for ${viewName}`));
        return;
      }

      // Guard: too many sources → warn, don't auto-expand
      if (responseView.sources.length > 15) {
        dispatch(notify(
            `View ${viewName} has ${responseView.sources.length} source tables — too large to auto-expand.`,
            'Expand anyway',
            () => dispatch(_doMergeViewLineage(dbNodeId, viewTableId, responseView, viewName, true)),
        ));
        return;
      }

      dispatch(_doMergeViewLineage(dbNodeId, viewTableId, responseView, viewName, false));
    } catch (e) {
      dispatch(notify(`No lineage derivable for ${viewName}`));
    } finally {
      _viewLineageInFlight.delete(key);
    }
  };

const _doMergeViewLineage = (dbNodeId, viewTableId, responseView, viewName, _forced) =>
  (dispatch, getState) => {
    const {map} = getState().lineageReducer;
    const dbNode = map.nodes.find((n) => n.id === dbNodeId);
    if (!dbNode) return;

    const deltas = deriveViewLineage(
        responseView,
        dbNodeId,
        viewTableId,
        map.nodes,
        map.mappings,
    );

    if (
      deltas.tablesToAdd.length === 0 &&
      deltas.mappingsToAdd.length === 0 &&
      deltas.viewElementsToMerge.length === 0
    ) {
      dispatch(notify(`No lineage derivable for ${viewName}`));
      return;
    }

    dispatch({
      type: MERGE_VIEW_LINEAGE,
      payload: {dbNodeId, viewTableId, viewName, ...deltas},
    });

    const n = deltas.tablesToAdd.length;
    const m = deltas.mappingsToAdd.length;
    dispatch(notify(
        `Added ${n} source table${n !== 1 ? 's' : ''} & ${m} column mapping${m !== 1 ? 's' : ''} for view ${viewName}`,
        'Undo',
        () => dispatch(undo()),
    ));
  };

// ---- Import ----
export const importLineage = (payload) => ({type: IMPORT_LINEAGE, payload});

export const uploadImportFile = (file) => async () => {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/lineage/import`,
      form,
      {headers: {'x-access-token': getAccessToken()}},
  );
  // The server returns HTTP 200 with {status:'error', error} for a rejected file
  // (bad headers, >2000 rows, unreadable) — surface it so the modal shows the reason.
  if (res.data && res.data.status === 'error') {
    throw new Error(res.data.error || 'Import failed');
  }
  return res.data.response;
};

export const downloadTemplateFile = () => async () => {
  const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/lineage/template`,
      {responseType: 'blob', headers: {'x-access-token': getAccessToken()}},
  );
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'lineage-template.xlsx';
  a.click();
  URL.revokeObjectURL(url);
};

// ---- History ----
export const undo = () => ({type: UNDO});
export const redo = () => ({type: REDO});
